import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Store, Copy, History as HistoryIcon } from "lucide-react";

const EMPTY_IMG =
  "https://images.pexels.com/photos/15164001/pexels-photo-15164001.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const History = () => {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    api
      .get("/history")
      .then((res) => setHistory(res.data))
      .catch(() => setHistory([]));
  }, []);

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch {
      toast.error("Couldn't access the clipboard.");
    }
  };

  return (
    <Layout title="History" subtitle="Every generation is auto-saved here">
      <div className="max-w-4xl" data-testid="history-page">
        {history === null ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full bg-zinc-800 rounded-2xl" />
            <Skeleton className="h-20 w-full bg-zinc-800 rounded-2xl" />
            <Skeleton className="h-20 w-full bg-zinc-800 rounded-2xl" />
          </div>
        ) : history.length === 0 ? (
          <div className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-10 text-center" data-testid="history-empty">
            <img src={EMPTY_IMG} alt="" className="h-40 w-full object-cover rounded-xl opacity-40 mb-6" />
            <HistoryIcon className="h-6 w-6 text-zinc-700 mx-auto mb-2" />
            <p className="text-zinc-400 text-sm">No generations yet.</p>
            <p className="text-zinc-600 text-xs font-mono mt-1">Create one from the Patch Notes or Store Copywriter tools.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="history-list">
            {history.map((h) => (
              <div
                key={h.id}
                className="group border border-zinc-800 bg-zinc-900/30 rounded-2xl overflow-hidden transition-colors duration-200 hover:border-teal-500/30"
                data-testid="history-item"
              >
                <div className="flex items-center gap-3 px-5 py-4">
                  <div className="h-9 w-9 shrink-0 rounded-xl bg-zinc-800 flex items-center justify-center text-teal-300">
                    {h.module_used.includes("Store") ? <Store className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-100 truncate font-medium">{h.title}</p>
                    <p className="text-[11px] text-zinc-500 font-mono">
                      {h.module_used} · {fmtDate(h.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => copy(h.output_text)}
                    data-testid="history-copy-btn"
                    className="flex items-center gap-1.5 text-xs border border-zinc-700 text-teal-300 rounded-xl px-3 py-1.5 hover:border-teal-500/40 hover:bg-teal-400/10 transition-colors duration-200"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </button>
                </div>
                <pre className="px-5 py-4 border-t border-zinc-800 text-xs text-zinc-400 font-mono whitespace-pre-wrap max-h-56 overflow-auto bg-zinc-950/50">
                  {h.output_text}
                </pre>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default History;
